/**
 * Sistema de partículas reutilizable para efectos visuales.
 * Usa object pooling de sprites de Phaser para rendimiento.
 */

// ─── Spritesheet de partículas predefinido ────────────────────────────────────

export const PARTICLE_SHEET = {
  path: 'particles/1.png',
  key: 'particles1',
  cols: 9,
  rows: 9,
  frameWidth: 512,
  frameHeight: 512,
} as const;

/** Nombres de cada frame del spritesheet (índice 0-80) */
export const PARTICLE_FRAME_NAMES: string[] = [
  'circle1', 'circle2', 'circle3', 'circle4', 'circle5',
  'dirt1', 'dirt2', 'dirt3',
  'fire1', 'fire2',
  'flame1', 'flame2', 'flame3', 'flame4', 'flame5', 'flame6',
  'flare1',
  'light1', 'light2', 'light3',
  'magic1', 'magic2', 'magic3', 'magic4', 'magic5',
  'muzzle1', 'muzzle2', 'muzzle3', 'muzzle4', 'muzzle5',
  'scorch1', 'scorch2', 'scorch3',
  'scratch1',
  'slash1', 'slash2', 'slash3', 'slash4',
  'smoke1', 'smoke2', 'smoke3', 'smoke4', 'smoke5', 'smoke6', 'smoke7', 'smoke8', 'smoke9', 'smoke10',
  'spark1', 'spark2', 'spark3', 'spark4', 'spark5', 'spark6', 'spark7',
  'star1', 'star2', 'star3', 'star4', 'star5', 'star6', 'star7', 'star8', 'star9',
  'symbol1', 'symbol2',
  'trace1', 'trace2', 'trace3', 'trace4', 'trace5', 'trace6', 'trace7',
  'twirl1', 'twirl2', 'twirl3',
  'window1', 'window2', 'window3', 'window4',
];

/** Busca el índice de un frame por nombre */
export function getParticleFrameIndex(name: string): number {
  return PARTICLE_FRAME_NAMES.indexOf(name);
}

// ─── Definición JSON de un efecto de partículas ───────────────────────────────

export interface ParticleOrbitDef {
  enabled: boolean;
  radiusX: number;
  radiusY: number;
  speed: number; // grados/s
  /** Fracción del radio al nacer (t=0) → radio completo al morir (t=1). Default 1. */
  radiusStart?: number;
}

export interface ParticleEffectDef {
  name: string;

  // Spritesheet
  spriteKey: string;
  frames: number[];

  // Emisión
  numParticles: number;
  emitRate: number; // partículas/s (0 = burst de numParticles)

  // Spawn area relativa al emisor
  spawnX: [number, number]; // [min, max]
  spawnY: [number, number];

  // Velocidad inicial px/s
  speedX: [number, number];
  speedY: [number, number];

  // Vida en ms
  life: [number, number];

  // Física
  gravityY: number; // px/s²
  friction: number; // 0-1 multiplicador por segundo
  bounce: number;

  // Rotación (grados/s)
  spin: [number, number];

  // Órbita
  orbit: ParticleOrbitDef;

  // Visual
  alpha: [number, number]; // [start, end]
  scale: [number, number]; // [start, end] multiplicador global
  frameScales: Record<number, number>; // escala base por frame (se multiplica con scale)
  blendMode: 'NORMAL' | 'ADD' | 'SCREEN';
  tint: number[]; // colores hex, se elige random por partícula

  // Loop
  loop: boolean;
  duration: number; // ms total (0 = infinito si loop)

  // Render — true = detrás del personaje, false/undefined = delante (default).
  behindEntity?: boolean;
  /**
   * 'fixed' (default): todas las partículas comparten this.depth.
   * 'orbit': depth por partícula recalculado cada frame según renderY vs
   * emitterY (delante si renderY > emitterY, atrás si menor). Crea la
   * ilusión 3D de un anillo en el piso para auras orbitando.
   */
  depthMode?: 'fixed' | 'orbit';
  /** Camera shake al spawn (sólo cuando se invoca sobre local player). */
  screenShake?: { duration: number; intensity: number };
  /** Camera flash al spawn (sólo cuando se invoca sobre local player). */
  screenFlash?: { duration: number; color?: number };
}

// ─── Estado interno de cada partícula ─────────────────────────────────────────

interface Particle {
  sprite: Phaser.GameObjects.Sprite;
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  angle: number;
  spinSpeed: number;
  orbitAngle: number;
  startAlpha: number;
  endAlpha: number;
  startScale: number;
  endScale: number;
  frameScale: number;
  tint: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function lerpN(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

const DEG2RAD = Math.PI / 180;

// ─── Valor por defecto ────────────────────────────────────────────────────────

export function createDefaultEffectDef(): ParticleEffectDef {
  return {
    name: 'Nuevo Efecto',
    spriteKey: '',
    frames: [0],
    numParticles: 20,
    emitRate: 10,
    spawnX: [-5, 5],
    spawnY: [-5, 5],
    speedX: [-10, 10],
    speedY: [-30, -60],
    life: [500, 1500],
    gravityY: 0,
    friction: 0,
    bounce: 0,
    spin: [0, 0],
    orbit: { enabled: false, radiusX: 30, radiusY: 15, speed: 180 },
    alpha: [1, 0],
    scale: [1, 0.5],
    frameScales: {},
    blendMode: 'ADD',
    tint: [0xffffff],
    loop: true,
    duration: 0,
    behindEntity: false,
    depthMode: 'fixed',
  };
}

// ─── Blend mode helper ───────────────────────────────────────────────────────

function toBlendMode(mode: string): Phaser.BlendModes {
  switch (mode) {
    case 'ADD':
      return Phaser.BlendModes.ADD;
    case 'SCREEN':
      return Phaser.BlendModes.SCREEN;
    default:
      return Phaser.BlendModes.NORMAL;
  }
}

// ─── Emitter ──────────────────────────────────────────────────────────────────

export class ParticleEmitter {
  private scene: Phaser.Scene;
  private def: ParticleEffectDef;
  private particles: Particle[] = [];
  private emitterX = 0;
  private emitterY = 0;
  private running = false;
  private elapsed = 0;
  private emitAccum = 0;
  private depth = 10000;
  /** Depth de referencia (típicamente del anchor/entity). Si depthMode='orbit', cada partícula se coloca en baseDepth±1. */
  private baseDepth = 10000;
  /** Máscara compartida aplicada a cada sprite del pool (clip por sprite del anchor). */
  private currentMask: Phaser.Display.Masks.BitmapMask | Phaser.Display.Masks.GeometryMask | null = null;

  constructor(scene: Phaser.Scene, def: ParticleEffectDef, x: number, y: number) {
    this.scene = scene;
    this.def = def;
    this.emitterX = x;
    this.emitterY = y;
    this.createPool();
  }

  // ── Pool ────────────────────────────────────────────────────────────────────

  private createPool(): void {
    const blend = toBlendMode(this.def.blendMode);
    for (let i = 0; i < this.def.numParticles; i++) {
      const frame = this.def.frames.length > 0 ? this.def.frames[0] : 0;
      const sprite = this.scene.add.sprite(0, 0, this.def.spriteKey, frame);
      sprite.setVisible(false);
      sprite.setBlendMode(blend);
      sprite.setDepth(this.depth);
      if (this.currentMask) sprite.setMask(this.currentMask);
      this.particles.push({
        sprite,
        alive: false,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        life: 0,
        maxLife: 1,
        angle: 0,
        spinSpeed: 0,
        orbitAngle: rand(0, 360),
        startAlpha: 1,
        endAlpha: 0,
        startScale: 1,
        endScale: 1,
        frameScale: 1,
        tint: 0xffffff,
      });
    }
  }

  // ── Emitir una partícula ────────────────────────────────────────────────────

  private emit(): void {
    const p = this.particles.find((pp) => !pp.alive);
    if (!p) return;

    const d = this.def;
    p.alive = true;
    p.x = this.emitterX + rand(d.spawnX[0], d.spawnX[1]);
    p.y = this.emitterY + rand(d.spawnY[0], d.spawnY[1]);
    p.vx = rand(d.speedX[0], d.speedX[1]);
    p.vy = rand(d.speedY[0], d.speedY[1]);
    p.maxLife = rand(d.life[0], d.life[1]);
    p.life = p.maxLife;
    p.angle = 0;
    p.spinSpeed = rand(d.spin[0], d.spin[1]);
    p.orbitAngle = rand(0, 360);
    p.startAlpha = d.alpha[0];
    p.endAlpha = d.alpha[1];
    p.startScale = d.scale[0];
    p.endScale = d.scale[1];
    p.tint = d.tint.length > 0 ? pick(d.tint) : 0xffffff;

    const frame = d.frames.length > 0 ? pick(d.frames) : 0;
    p.frameScale = d.frameScales[frame] ?? 1;
    p.sprite.setFrame(frame);
    p.sprite.setTint(p.tint);
    p.sprite.setBlendMode(toBlendMode(d.blendMode));
    p.sprite.setVisible(true);
  }

  // ── Update ──────────────────────────────────────────────────────────────────

  update(delta: number): void {
    const dt = delta / 1000; // segundos
    const d = this.def;

    // Duración total — running pasa a false al consumirla (one-shot),
    // pero las partículas vivas siguen actualizándose hasta morir.
    if (this.running && d.duration > 0) {
      this.elapsed += delta;
      if (this.elapsed >= d.duration) {
        if (d.loop) {
          this.elapsed = 0;
        } else {
          this.running = false;
        }
      }
    }

    // Emisión — sólo si running
    if (this.running) {
      if (d.emitRate > 0) {
        this.emitAccum += delta;
        const interval = 1000 / d.emitRate;
        while (this.emitAccum >= interval) {
          this.emit();
          this.emitAccum -= interval;
        }
      }
    }

    // Actualizar partículas vivas
    for (const p of this.particles) {
      if (!p.alive) continue;

      p.life -= delta;
      if (p.life <= 0) {
        p.alive = false;
        p.sprite.setVisible(false);
        continue;
      }

      const t = 1 - p.life / p.maxLife; // 0 → 1

      // Física
      p.vy += d.gravityY * dt;
      if (d.friction > 0) {
        const f = Math.pow(1 - d.friction, dt);
        p.vx *= f;
        p.vy *= f;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      // Spin
      p.angle += p.spinSpeed * dt;

      // Órbita
      let renderX = p.x;
      let renderY = p.y;
      // Front/back de la órbita para el depth dinámico. Fallback (sin orbit):
      // por Y absoluto vs el emitter.
      let orbitFront = renderY > this.emitterY;
      if (d.orbit.enabled) {
        p.orbitAngle += d.orbit.speed * dt;
        const rad = p.orbitAngle * DEG2RAD;
        // Radio que crece con la edad (t): angosto al nacer, ancho al morir.
        // Combinado con velocidad vertical → tornado que se ensancha arriba.
        const rGrow = lerpN(d.orbit.radiusStart ?? 1, 1, t);
        renderX = this.emitterX + Math.cos(rad) * d.orbit.radiusX * rGrow + (p.x - this.emitterX);
        renderY = this.emitterY + Math.sin(rad) * d.orbit.radiusY * rGrow + (p.y - this.emitterY);
        // Front/back por FASE de la órbita (sin > 0 = mitad cercana, el barrido
        // que viene hacia la cámara / der→izq), NO por renderY absoluto. Así el
        // arco cercano pasa SIEMPRE por delante del char (body+head) aunque la
        // partícula derive hacia arriba (vy) o el anillo esté a la altura de los
        // pies — da la profundidad 3D del aura envolviendo al personaje.
        orbitFront = Math.sin(rad) > 0;
      }

      // Visual
      const alpha = lerpN(p.startAlpha, p.endAlpha, t);
      const scale = lerpN(p.startScale, p.endScale, t) * p.frameScale;

      p.sprite.setPosition(renderX, renderY);
      p.sprite.setAlpha(alpha);
      p.sprite.setScale(scale);
      p.sprite.setAngle(p.angle);

      // Depth dinámico (modo 'orbit'): partículas en la mitad delantera de la
      // órbita van por ENCIMA del char (baseDepth + 1); las de la mitad trasera,
      // por DETRÁS (baseDepth - 1). Crea la ilusión 3D del aura envolviendo al
      // personaje (ver `orbitFront` arriba: se decide por fase, no por Y).
      if (d.depthMode === 'orbit') {
        p.sprite.setDepth(this.baseDepth + (orbitFront ? 1 : -1));
      }
    }
  }

  // ── Control ─────────────────────────────────────────────────────────────────

  start(): void {
    this.running = true;
    this.elapsed = 0;
    this.emitAccum = 0;

    // Burst mode
    if (this.def.emitRate <= 0) {
      for (let i = 0; i < this.def.numParticles; i++) {
        this.emit();
      }
    }
  }

  stop(): void {
    this.running = false;
    for (const p of this.particles) {
      p.alive = false;
      p.sprite.setVisible(false);
    }
  }

  setPosition(x: number, y: number): void {
    this.emitterX = x;
    this.emitterY = y;
  }

  setDepth(depth: number): void {
    this.depth = depth;
    for (const p of this.particles) {
      p.sprite.setDepth(depth);
    }
  }

  /**
   * Setea la depth de referencia para el modo 'orbit'. Las partículas se
   * colocarán en baseDepth±1 según su posición Y relativa al emitter.
   */
  setBaseDepth(baseDepth: number): void {
    this.baseDepth = baseDepth;
  }

  /**
   * Mueve cada partícula viva por (dx, dy) en world space. Usado por
   * `ParticleManager` para implementar el "entityFollow" (las partículas
   * arrastran parcialmente con el anchor cuando éste se desplaza).
   * El render se propaga automáticamente porque `update()` ya posiciona
   * cada sprite en `(p.x, p.y)` cada frame.
   */
  shiftAliveParticles(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    for (const p of this.particles) {
      if (!p.alive) continue;
      p.x += dx;
      p.y += dy;
    }
  }

  /**
   * Aplica una máscara (BitmapMask o GeometryMask) a todas las partículas
   * del pool. La referencia queda guardada para reaplicarla a sprites nuevos
   * creados por `reconfigure()`. Pasar null limpia la máscara.
   */
  setMask(
    mask: Phaser.Display.Masks.BitmapMask | Phaser.Display.Masks.GeometryMask | null
  ): void {
    this.currentMask = mask;
    for (const p of this.particles) {
      if (mask) p.sprite.setMask(mask);
      else p.sprite.clearMask();
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Reconfigura el emitter con una nueva definición (hot-reload para el editor) */
  reconfigure(def: ParticleEffectDef): void {
    this.stop();
    // Matar todas las partículas vivas
    for (const p of this.particles) {
      p.alive = false;
      p.sprite.setVisible(false);
    }
    // Ajustar pool size
    const blend = toBlendMode(def.blendMode);
    if (def.numParticles > this.particles.length) {
      for (let i = this.particles.length; i < def.numParticles; i++) {
        const frame = def.frames.length > 0 ? def.frames[0] : 0;
        const sprite = this.scene.add.sprite(0, 0, def.spriteKey, frame);
        sprite.setVisible(false);
        sprite.setBlendMode(blend);
        sprite.setDepth(this.depth);
        if (this.currentMask) sprite.setMask(this.currentMask);
        this.particles.push({
          sprite,
          alive: false,
          x: 0, y: 0, vx: 0, vy: 0,
          life: 0, maxLife: 1,
          angle: 0, spinSpeed: 0, orbitAngle: 0,
          startAlpha: 1, endAlpha: 0,
          startScale: 1, endScale: 1, frameScale: 1,
          tint: 0xffffff,
        });
      }
    } else if (def.numParticles < this.particles.length) {
      const excess = this.particles.splice(def.numParticles);
      for (const p of excess) {
        p.sprite.destroy();
      }
    }
    // Actualizar spriteKey en sprites existentes
    for (const p of this.particles) {
      if (def.spriteKey && this.scene.textures.exists(def.spriteKey)) {
        p.sprite.setTexture(def.spriteKey);
      }
      p.sprite.setBlendMode(blend);
    }
    this.def = def;
  }

  destroy(): void {
    for (const p of this.particles) {
      p.sprite.destroy();
    }
    this.particles = [];
    this.running = false;
  }
}

// ─── Router v1/v2 (Fase 1) ────────────────────────────────────────────────────
// Las defs nuevas viven en shared/data/particleEffects.ts con `version: 2` y
// se reproducen vía ParticleManager + adapter al ParticleEmitter nativo de
// Phaser 3.60+ (Fase 2). Los consumers viejos (ParticleEditorScene, AuthUI)
// siguen instanciando `new ParticleEmitter(defV1, ...)` y este archivo no se
// modifica en su comportamiento.
//
// Este factory permite a un consumer pasar cualquier def y obtener un emitter:
//   - def v1 (sin `version`) → instancia ParticleEmitter actual.
//   - def v2 (con `version: 2`) → stub que warnea hasta F2.

import { isParticleEffectV2 } from '@shared/types/particles';
import type { ParticleEffectDefV2 } from '@shared/types/particles';

export type AnyParticleEffectDef = ParticleEffectDef | ParticleEffectDefV2;

export interface EmitterLike {
  start(): void;
  stop(): void;
  setPosition(x: number, y: number): void;
  setDepth(depth: number): void;
  update(delta: number): void;
  destroy(): void;
  isRunning(): boolean;
}

export function createEmitterFromAnyDef(
  scene: Phaser.Scene,
  def: AnyParticleEffectDef,
  x: number,
  y: number
): EmitterLike | null {
  if (isParticleEffectV2(def as { version?: number })) {
    // F2: phaserNativeEmitterAdapter(scene, def as ParticleEffectDefV2, x, y).
    console.warn(
      `[ParticleSystem] def v2 "${(def as ParticleEffectDefV2).id}" — motor v2 todavía no implementado (F2).`
    );
    return null;
  }
  return new ParticleEmitter(scene, def as ParticleEffectDef, x, y);
}
